"use strict";

exports.__esModule = true;
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _slateReact = require("slate-react");
var _slate = require("slate");
var _isHotkey = require("is-hotkey");
var _helpers = require("../../../helpers");
var _icon = _interopRequireDefault(require("../../../../fonts/icon"));
var _schema = require("./schema");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * Define the default node type.
 *
 * @type {String}
 */

const DEFAULT_NODE = "paragraph";

/**
 * Define hotkey matchers.
 *
 * @type {Function}
 */

const isBoldHotkey = (0, _isHotkey.isKeyHotkey)("mod+b");
const isItalicHotkey = (0, _isHotkey.isKeyHotkey)("mod+i");
const isUnderlinedHotkey = (0, _isHotkey.isKeyHotkey)("mod+u");
const isCodeHotkey = (0, _isHotkey.isKeyHotkey)("mod+`");

/**
 * A change helper to standardize wrapping links.
 *
 * @param {Change} change
 * @param {String} href
 */

function wrapLink(change, href) {
  change.wrapInline({
    type: "link",
    data: {
      href
    }
  });
  change.moveToEnd();
}

/**
 * A change helper to standardize unwrapping links.
 *
 * @param {Change} change
 */

function unwrapLink(change) {
  change.unwrapInline("link");
}

/**
 * The rich text example.
 *
 * @type {Component}
 */

const initialValue = _slate.Value.fromJSON({
  object: "value",
  document: {
    object: "document",
    data: {},
    nodes: [{
      object: "block",
      type: "paragraph",
      data: {},
      nodes: [{
        object: "text",
        text: "",
        marks: []
      }]
    }]
  }
});
class RichTextEditor extends _react.default.Component {
  constructor(...args) {
    super(...args);
    /**
     * Deserialize the initial editor value.
     *
     * @type {Object}
     */
    _defineProperty(this, "state", {
      value: (0, _helpers.isValidJson)(this.props.value) ? _slate.Value.fromJSON(JSON.parse(this.props.value)) : initialValue
    });
    /**
     * Check if the current selection has a mark with `type` in it.
     *
     * @param {String} type
     * @return {Boolean}
     */
    _defineProperty(this, "hasMark", type => {
      const {
        value
      } = this.state;
      return value.activeMarks.some(mark => mark.type == type);
    });
    /**
     * Check if the any of the currently selected blocks are of `type`.
     *
     * @param {String} type
     * @return {Boolean}
     */
    _defineProperty(this, "hasBlock", type => {
      const {
        value
      } = this.state;
      return value.blocks.some(node => node.type == type);
    });
    /**
     * Check whether the current selection has a link in it.
     *
     * @return {Boolean} hasLinks
     */
    _defineProperty(this, "hasLinks", () => {
      const {
        value
      } = this.state;
      return value.inlines.some(inline => inline.type == "link");
    });
    /**
     * On change, save the new `value`.
     *
     * @param {Change} change
     */
    _defineProperty(this, "onChange", ({
      value
    }) => {
      this.setState({
        value
      });
      const {
        onChange
      } = this.props;
      if (onChange) onChange(JSON.stringify(value.toJSON()));
    });
    /**
     * Store a reference to the `editor`.
     *
     * @param {Editor} editor
     */
    _defineProperty(this, "ref", editor => {
      this.editor = editor;
    });
    /**
     * Render a mark-toggling toolbar button.
     *
     * @param {String} type
     * @param {String} icon
     * @return {Element}
     */
    _defineProperty(this, "renderMarkButton", (type, icon) => {
      const isActive = this.hasMark(type);
      return /*#__PURE__*/_react.default.createElement("button", {
        type: "button",
        className: "btn btn-rich-text d-flex align-items-center justify-content-center " + (isActive ? "btn-primary" : "btn-link"),
        onMouseDown: event => this.onClickMark(event, type)
      }, icon);
    });
    /**
     * Render a block-toggling toolbar button.
     *
     * @param {String} type
     * @param {String} icon
     * @return {Element}
     */
    _defineProperty(this, "renderBlockButton", (type, icon) => {
      let isActive = this.hasBlock(type);
      if (["numbered-list", "bulleted-list"].includes(type)) {
        const {
          value: {
            document,
            blocks
          }
        } = this.state;
        if (blocks.size > 0) {
          const parent = document.getParent(blocks.first().key);
          isActive = this.hasBlock("list-item") && parent && parent.type === type;
        }
      }
      return /*#__PURE__*/_react.default.createElement("button", {
        type: "button",
        className: "btn btn-rich-text d-flex align-items-center justify-content-center " + (isActive ? "btn-primary" : "btn-link"),
        onMouseDown: event => this.onClickBlock(event, type)
      }, icon);
    });
    /**
     * On key down, if it's a formatting command toggle a mark.
     *
     * @param {Event} event
     * @param {Change} change
     * @return {Change}
     */
    _defineProperty(this, "onKeyDown", (event, change, next) => {
      let mark;
      if (isBoldHotkey(event)) {
        mark = "bold";
      } else if (isItalicHotkey(event)) {
        mark = "italic";
      } else if (isUnderlinedHotkey(event)) {
        mark = "underlined";
      } else if (isCodeHotkey(event)) {
        mark = "code";
      } else {
        return next();
      }
      event.preventDefault();
      change.toggleMark(mark);
    });
    /**
     * When a mark button is clicked, toggle the current mark.
     *
     * @param {Event} event
     * @param {String} type
     */
    _defineProperty(this, "onClickMark", (event, type) => {
      event.preventDefault();
      this.editor.change(change => {
        change.toggleMark(type);
      });
    });
    /**
     * When a block button is clicked, toggle the block type.
     *
     * @param {Event} event
     * @param {String} type
     */
    _defineProperty(this, "onClickBlock", (event, type) => {
      event.preventDefault();
      this.editor.change(change => {
        const {
          value
        } = change;
        const {
          document
        } = value;

        // Handle everything but list buttons.
        if (type != "bulleted-list" && type != "numbered-list") {
          const isActive = this.hasBlock(type);
          const isList = this.hasBlock("list-item");
          if (isList) {
            change.setBlocks(isActive ? DEFAULT_NODE : type).unwrapBlock("bulleted-list").unwrapBlock("numbered-list");
          } else {
            change.setBlocks(isActive ? DEFAULT_NODE : type);
          }
        } else {
          // Handle the extra wrapping required for list buttons.
          const isList = this.hasBlock("list-item");
          const isType = value.blocks.some(block => {
            return !!document.getClosest(block.key, parent => parent.type == type);
          });
          if (isList && isType) {
            change.setBlocks(DEFAULT_NODE).unwrapBlock("bulleted-list").unwrapBlock("numbered-list");
          } else if (isList) {
            change.unwrapBlock(type == "bulleted-list" ? "numbered-list" : "bulleted-list").wrapBlock(type);
          } else {
            change.setBlocks("list-item").wrapBlock(type);
          }
        }
      });
    });
    /**
     * When clicking a link, if the selection has a link in it, remove the link.
     * Otherwise, add a new link with an href and text.
     *
     * @param {Event} event
     */
    _defineProperty(this, "onClickLink", event => {
      event.preventDefault();
      this.editor.change(change => {
        const {
          value
        } = change;
        const hasLinks = this.hasLinks();
        if (hasLinks) {
          change.call(unwrapLink);
        } else if (value.selection.isExpanded) {
          const href = window.prompt("Enter the URL of the link:");
          if (href === null) {
            return;
          }
          change.call(wrapLink, href);
        } else {
          const href = window.prompt("Enter the URL of the link:");
          if (href === null) {
            return;
          }
          const text = window.prompt("Enter the text for the link:");
          if (text === null) {
            return;
          }
          change.insertText(text).moveFocusBackward(text.length).call(wrapLink, href);
        }
      });
    });
    /**
     * On paste, if the text is a link, wrap the selection in a link.
     *
     * @param {Event} event
     * @param {Change} change
     * @param {Function} next
     */
    _defineProperty(this, "onPaste", (event, change, next) => {
      if (change.value.selection.isCollapsed) return next();

      // eslint-disable-next-line no-undef
      const transfer = getEventTransfer(event);
      const {
        type,
        text
      } = transfer;
      if (type != "text" && type != "html") return next();
      // eslint-disable-next-line no-undef
      if (!isUrl(text)) return next();
      if (this.hasLinks()) {
        change.call(unwrapLink);
      }
      change.call(wrapLink, text);
    });
  }
  render() {
    return /*#__PURE__*/_react.default.createElement("div", {
      className: "form-control form-control-editor"
    }, /*#__PURE__*/_react.default.createElement(_slateReact.Editor, {
      className: "slate-editor",
      spellCheck: true
      // autoFocus
      ,
      ref: this.ref,
      value: this.state.value,
      onChange: this.onChange,
      onKeyDown: this.onKeyDown,
      onPaste: this.onPaste,
      renderNode: _schema.renderNode,
      renderMark: _schema.renderMark
    }), /*#__PURE__*/_react.default.createElement("div", {
      className: "btn-toolbar slate-editor-toolbar"
    }, /*#__PURE__*/_react.default.createElement("div", {
      className: "btn-group btn-group-sm"
    }, this.renderMarkButton("bold", /*#__PURE__*/_react.default.createElement(_icon.default, {
      icon: "format_bold",
      size: 16
    })), this.renderMarkButton("italic", /*#__PURE__*/_react.default.createElement(_icon.default, {
      icon: "format_italic",
      size: 16
    })), this.renderMarkButton("underlined", /*#__PURE__*/_react.default.createElement(_icon.default, {
      icon: "format_underlined",
      size: 16
    }))), /*#__PURE__*/_react.default.createElement("div", {
      className: "btn-group btn-group-sm"
    }, this.renderBlockButton("heading-one", /*#__PURE__*/_react.default.createElement(_icon.default, {
      icon: "looks_one",
      size: 16
    })), this.renderBlockButton("heading-two", /*#__PURE__*/_react.default.createElement(_icon.default, {
      icon: "looks_two",
      size: 16
    })), this.renderBlockButton("numbered-list", /*#__PURE__*/_react.default.createElement(_icon.default, {
      icon: "format_list_numbered",
      size: 16
    })), this.renderBlockButton("bulleted-list", /*#__PURE__*/_react.default.createElement(_icon.default, {
      icon: "format_list_bulleted",
      size: 16
    })), /*#__PURE__*/_react.default.createElement("button", {
      type: "button",
      className: "btn btn-rich-text d-flex align-items-center justify-content-center " + (this.hasLinks() ? "btn-primary" : "btn-link"),
      onMouseDown: this.onClickLink
    }, /*#__PURE__*/_react.default.createElement(_icon.default, {
      icon: "link",
      size: 16
    })))));
  }
}
var _default = exports.default = RichTextEditor;
module.exports = exports.default;